import {useCallback, useReducer} from 'react';
import {IAlert} from '../../../../../actions/proto';
import {priority} from '@priority-software/priority-proto-api/lib';
import {AlertMappedItem} from '../../../../../reducer/types';
import {AlertFields} from '../../../../../enums';
import {BusinessRuleAction, emptyRecipientFields} from '../../../../../constants';
import {createMessageDictionary, getActiveMessageLang} from './utils';
import {TAlertMessage, TAlertMessageDictionary} from '../../../../../types';

type Props = {
    openRule: AlertMappedItem;
    onRuleUpdate: (data: Partial<IAlert>) => void;
};

export enum ActionTypes {
    CHANGE_MESSAGE = 'CHANGE_MESSAGE',
    CHANGE_MESSAGE_LANG = 'CHANGE_MESSAGE_LANG',
    UPDATE_RULE_FIELD = 'UPDATE_RULE_FIELD'
}

export const businessRuleTemplateReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.CHANGE_MESSAGE_LANG:
            return {...state, activeMessageLang: action.payload.activeMessageLang};

        case ActionTypes.UPDATE_RULE_FIELD:
            return {...state, ...action.payload.data};

        case ActionTypes.CHANGE_MESSAGE:
            const currLang = state.activeMessageLang;

            const currMessage =
                state.Messages[currLang] ||
                new priority.netitems.form.AlertMessage({
                    Message: '',
                    Language: state.activeMessageLang,
                    Subject: ''
                });

            const Messages = {
                ...state.Messages,
                [currLang]: {
                    ...currMessage,
                    [action.payload.field]: action.payload.value
                }
            };
            action.onRuleUpdate({Messages: Object.values(Messages)});

            return {...state, Messages};
        default:
            return state;
    }
};

type State = {
    Messages: TAlertMessageDictionary;
    activeMessageLang: string;
    MailType?: number | null;
    WebHook?: number | null;
    RecipientMail?: string[];
    RecipientField?: number | null;
    RecipientGroup?: (number | Long)[] | null;
    RecipientUser?: (number | Long)[] | null;
    ExtFile?: boolean;
    Pdf?: boolean;
    WordTemplates?: boolean;
    Document?: number | Long | null;
    DocumentFormat?: number | Long | null;
    ToStatus?: number | Long | null;
    CoverLetters?: TAlertMessageDictionary;
};

type Action<T, P = undefined, C = undefined> = {
    type: T;
    payload?: P;
    onRuleUpdate?: C;
};

type BusinessRuleTemplateAction =
    | Action<
          ActionTypes.CHANGE_MESSAGE,
          {field: string; value: string | number | boolean | string[]},
          (data: Partial<IAlert>) => void
      >
    | Action<ActionTypes.CHANGE_MESSAGE_LANG, {activeMessageLang: string}>
    | Action<
          ActionTypes.UPDATE_RULE_FIELD,
          {data: Partial<State>},
          (data: Partial<IAlert>) => void
      >;

type ReducerType = (state: State, action: BusinessRuleTemplateAction) => State;

export const useBusinessRuleTemplatesData = (props: Props) => {
    const {openRule, onRuleUpdate} = props;
    const {CoverLetters, MessageType} = openRule;

    const [state, dispatch] = useReducer<ReducerType, State>(
        businessRuleTemplateReducer,
        {
            Messages: createMessageDictionary(openRule.Messages),
            activeMessageLang: getActiveMessageLang(),
            MailType: Number(openRule.MailType),
            WebHook: Number(openRule.WebHook),
            RecipientMail: openRule.RecipientMail,
            RecipientField: Number(openRule.RecipientField),
            RecipientGroup: openRule.RecipientGroup,
            RecipientUser: openRule.RecipientUser,
            ExtFile: openRule.ExtFile,
            Pdf: openRule.Pdf,
            WordTemplates: openRule.WordTemplates,
            Document: openRule.Document,
            DocumentFormat: openRule.DocumentFormat,
            ToStatus: openRule?.BPM?.ToStatus,
            CoverLetters:
                MessageType === BusinessRuleAction.EmailMsg
                    ? createMessageDictionary(CoverLetters || [])
                    : ({} as TAlertMessageDictionary) // CoverLetters are only relevant for EmailMsg type
        },
        arg => arg
    );

    const emails = state.RecipientMail;

    const currentMessageData: TAlertMessage = state.Messages[state.activeMessageLang];

    const handleMessageData = useCallback((field: string, value: string) => {
        dispatch({type: ActionTypes.CHANGE_MESSAGE, payload: {field, value}, onRuleUpdate});
    }, []);

    const insertShortcodeData = useCallback((value: string, fieldName: string) => {
        dispatch({
            type: ActionTypes.CHANGE_MESSAGE,
            payload: {field: fieldName, value},
            onRuleUpdate
        });
    }, []);

    const onMessageLangChange = useCallback((activeMessageLang: string) => {
        dispatch({type: ActionTypes.CHANGE_MESSAGE_LANG, payload: {activeMessageLang}});
    }, []);

    const updateRuleField = useCallback(
        (value: string | number | boolean | Array<string>, field: string) => {
            let payload = {[field]: value};
            if (field === AlertFields.MailType) {
                payload = {...emptyRecipientFields, ...payload};
            }
            dispatch({type: ActionTypes.UPDATE_RULE_FIELD, payload: {data: payload}});
            onRuleUpdate(payload);
        },
        [onRuleUpdate]
    );

    const addEmailValues = useCallback(
        (email: string) => {
            updateRuleField(Array.from(new Set([...emails, email])), AlertFields.RecipientMail);
        },
        [updateRuleField, emails]
    );

    const removeEmailValue = useCallback(
        (email: string) => {
            updateRuleField(
                emails.filter(item => email !== item),
                AlertFields.RecipientMail
            );
        },
        [updateRuleField, emails]
    );

    const BPM = openRule.BPM;

    const changeStatus = useCallback(
        (data: number, name: string) => {
            const bpm = {BPM: {...(BPM || {}), [name]: data}};
            dispatch({type: ActionTypes.UPDATE_RULE_FIELD, payload: {data: {[name]: data}}});
            onRuleUpdate(bpm);
        },
        [updateRuleField, BPM, onRuleUpdate]
    );

    const changeCoverLetter = useCallback(
        (CoverLetters: TAlertMessageDictionary) => {
            dispatch({
                type: ActionTypes.UPDATE_RULE_FIELD,
                payload: {data: {CoverLetters}}
            });
            onRuleUpdate({CoverLetters: Object.values(CoverLetters)});
        },
        [onRuleUpdate]
    );

    return {
        state: {...state, currentMessageData},
        handlers: {
            handleMessageData,
            onMessageLangChange,
            insertShortcodeData,
            updateRuleField,
            addEmailValues,
            removeEmailValue,
            changeStatus,
            changeCoverLetter
        }
    };
};
